import { useEffect, useState } from 'react';
import type { HealthBand, HealthScore } from '@hospasense/contracts';
import { fetchHealthScores, refreshHealthScores, reviewHealthScore } from '../lib/queries-s5.js';
import { useAuth } from '../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorState,
  EmptyState,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  StatusPill,
  Textarea,
  useToast,
} from '../components/ui/index.js';

const BAND_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  healthy: 'success',
  watch: 'warning',
  at_risk: 'error',
};
const BAND_LABEL: Record<string, string> = {
  healthy: 'Healthy',
  watch: 'Watch',
  at_risk: 'At risk',
};

export function HealthPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canReview = hasPermission('health.review');

  const [scores, setScores] = useState<HealthScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<HealthScore | null>(null);
  const [reviewBand, setReviewBand] = useState<HealthBand>('watch');
  const [reviewNote, setReviewNote] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setScores(await fetchHealthScores());
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const n = await refreshHealthScores();
      toast.success(`Recomputed ${n} organisation score(s)`);
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setRefreshing(false);
    }
  };

  const submitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewTarget) return;
    setSavingReview(true);
    try {
      await reviewHealthScore(reviewTarget.organisationId, reviewBand, reviewNote);
      toast.success('Review recorded beside the computed score');
      setReviewTarget(null);
      setReviewNote('');
      await load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSavingReview(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="health-page">
      <PageHeader
        title="Customer Health"
        subtitle="Explainable, advisory-only scores. Every score shows its factors, weights and confidence; a low score can never suspend, bill or change entitlements — the only automatic follow-up is a human being notified."
        actions={
          <Button variant="secondary" loading={refreshing} onClick={() => void refresh()}>
            Recompute now
          </Button>
        }
      />
      {error && <ErrorState description={error} onRetry={() => void load()} />}
      {loading ? (
        <LoadingState label="Loading health scores…" />
      ) : scores.length === 0 ? (
        <EmptyState
          title="No health snapshots yet"
          description="Recompute now, or wait for the worker's scheduled refresh."
        />
      ) : (
        <div className="space-y-3">
          {scores.map((s) => (
            <Card key={s.organisationId} data-testid={`health-${s.organisationId}`}>
              <CardBody>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <p
                      className="w-14 text-2xl font-bold tabular-nums text-text-primary"
                      aria-label={`Score ${s.score ?? 'unknown'}`}
                    >
                      {s.score ?? '—'}
                    </p>
                    <div>
                      <h2 className="text-sm font-semibold text-text-primary">
                        {s.organisationName ?? s.organisationId}
                      </h2>
                      <p className="text-xs text-text-muted">
                        Confidence {(s.confidence * 100).toFixed(0)}% · computed{' '}
                        {new Date(s.computedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.band && <StatusPill tone={BAND_TONE[s.band]!} label={BAND_LABEL[s.band]!} />}
                    {s.reviewBand && (
                      <Badge tone={BAND_TONE[s.reviewBand]!}>
                        reviewed: {BAND_LABEL[s.reviewBand]}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-expanded={expanded === s.organisationId}
                      onClick={() =>
                        setExpanded(expanded === s.organisationId ? null : s.organisationId)
                      }
                    >
                      {expanded === s.organisationId ? 'Hide factors' : 'Why this score?'}
                    </Button>
                    {canReview && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setReviewTarget(s);
                          setReviewBand(s.reviewBand ?? s.band ?? 'watch');
                        }}
                      >
                        Record review
                      </Button>
                    )}
                  </div>
                </div>
                {s.reviewNote && (
                  <p className="mt-2 rounded-md bg-bg-subtle px-3 py-2 text-xs text-text-secondary">
                    <span className="font-semibold">{s.reviewedByDisplayName ?? 'Reviewer'}:</span>{' '}
                    {s.reviewNote}
                  </p>
                )}
                {expanded === s.organisationId && (
                  <ul className="mt-3 space-y-2 border-t border-border pt-3">
                    {s.factors.map((f) => (
                      <li key={f.key} className="flex items-center gap-3 text-xs">
                        <span className="w-40 shrink-0 font-medium text-text-primary">
                          {f.title}
                        </span>
                        <span className="w-14 shrink-0 tabular-nums text-text-secondary">
                          {f.score === null ? 'no data' : `${f.score}/100`}
                        </span>
                        <span className="w-20 shrink-0 text-text-muted">
                          weight {(f.weight * 100).toFixed(0)}%
                        </span>
                        <span className="min-w-0 flex-1 text-text-secondary">{f.basis}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={reviewTarget !== null}
        title={`Record review — ${reviewTarget?.organisationName ?? ''}`}
        onClose={() => setReviewTarget(null)}
      >
        <form onSubmit={(e) => void submitReview(e)} className="space-y-3">
          <p className="text-xs text-text-muted">
            A review is a human judgement recorded beside the computed number. It never changes the
            computation and is fully audited.
          </p>
          <Select
            label="Review band"
            value={reviewBand}
            onChange={(e) => setReviewBand(e.target.value as HealthBand)}
          >
            <option value="healthy">Healthy</option>
            <option value="watch">Watch</option>
            <option value="at_risk">At risk</option>
          </Select>
          <Textarea
            label="Note (required, min 10 characters)"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            minLength={10}
            required
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={() => setReviewTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={savingReview}>
              Record review
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
